import { playerNetOnHole, stablefordPts } from '@/lib/games/engine/helpers';
import type { CourseHole } from '@/lib/db/types';
import type { RoundPlayer } from '@/lib/games/engine/types';

export function teamBestNet(
  teamKeys: string[],
  hi: number,
  players: RoundPlayer[],
  holes: CourseHole[],
  scores: Record<string, (number | null)[]>,
  formatId: string,
  handicapAllowance?: number | null,
): number | null {
  const vals = teamKeys.map((key) => {
    const p = players.find((x) => x.playerKey === key);
    if (!p) return null;
    return playerNetOnHole(p, hi, holes, scores[key] || [], formatId, players, handicapAllowance);
  }).filter((v) => v != null) as number[];
  return vals.length ? Math.min(...vals) : null;
}

export function teamWorstNet(
  teamKeys: string[],
  hi: number,
  players: RoundPlayer[],
  holes: CourseHole[],
  scores: Record<string, (number | null)[]>,
  formatId: string,
  handicapAllowance?: number | null,
): number | null {
  const vals = teamKeys.map((key) => {
    const p = players.find((x) => x.playerKey === key);
    if (!p) return null;
    return playerNetOnHole(p, hi, holes, scores[key] || [], formatId, players, handicapAllowance);
  }).filter((v) => v != null) as number[];
  return vals.length ? Math.max(...vals) : null;
}

export function compareTeamsHolesWon(
  teams: string[][],
  players: RoundPlayer[],
  holes: CourseHole[],
  scores: Record<string, (number | null)[]>,
  compare: (a: number, b: number) => boolean,
): number {
  let diff = 0;
  holes.forEach((_, hi) => {
    const a = teamBestNet(teams[0], hi, players, holes, scores, 'fourball');
    const b = teamBestNet(teams[1], hi, players, holes, scores, 'fourball');
    if (a == null || b == null) return;
    if (compare(a, b)) diff++;
    else if (compare(b, a)) diff--;
  });
  return diff;
}

export function vegasHoleDiff(
  teams: string[][],
  hi: number,
  scores: Record<string, (number | null)[]>,
): number | null {
  const gA = teams[0].map((k) => scores[k]?.[hi]).filter((s) => s != null).sort((a, b) => (a as number) - (b as number));
  const gB = teams[1].map((k) => scores[k]?.[hi]).filter((s) => s != null).sort((a, b) => (a as number) - (b as number));
  if (gA.length < 2 || gB.length < 2) return null;
  const numA = (gA[0] as number) * 10 + (gA[1] as number);
  const numB = (gB[0] as number) * 10 + (gB[1] as number);
  return numB - numA;
}

export function teamStablefordTotal(
  teamKeys: string[],
  players: RoundPlayer[],
  holes: CourseHole[],
  scores: Record<string, (number | null)[]>,
): number {
  let pts = 0;
  holes.forEach((h, hi) => {
    const net = teamBestNet(teamKeys, hi, players, holes, scores, 'team-stableford');
    if (net == null) return;
    pts += stablefordPts(net - h.par);
  });
  return pts;
}

export function scrambleTeamTotals(
  teams: string[][],
  teamScores: Record<string, (number | null)[]>,
  holes: CourseHole[],
): { team0: number; team1: number } {
  let t0 = 0;
  let t1 = 0;
  holes.forEach((h, hi) => {
    const s0 = teamScores['0']?.[hi];
    const s1 = teamScores['1']?.[hi];
    if (s0 != null) t0 += s0 - h.par;
    if (s1 != null) t1 += s1 - h.par;
  });
  return { team0: t0, team1: t1 };
}

export function ryderCupTeamPoints(
  teams: string[][],
  players: RoundPlayer[],
  holes: CourseHole[],
  scores: Record<string, (number | null)[]>,
): { teamAPoints: number; teamBPoints: number } {
  let a = 0;
  let b = 0;
  holes.forEach((_, hi) => {
    const na = teamBestNet(teams[0], hi, players, holes, scores, 'ryder-cup');
    const nb = teamBestNet(teams[1], hi, players, holes, scores, 'ryder-cup');
    if (na == null || nb == null) return;
    if (na < nb) a += 1;
    else if (nb < na) b += 1;
    else { a += 0.5; b += 0.5; }
  });
  return { teamAPoints: a, teamBPoints: b };
}
